<template>
  <LazyDrawer position="right" :is-open="tallyTray" :hide-scroll="false" class="flex">
    <template #header>
      <Stack tag="div" align="start" justify="end">
        <button @click="closeTray()" class="text-charcoal-default justify-self-end">
          <Icon name="plus-thick" filled class="text-charcoal-default rotate-45" :size="16" />
        </button>
      </Stack>

      <Stack direction="col" gap="xs">
        <Heading tag="h3" size="xs" class="mt-xs" data-test-id="tallyPanelTitle">
          {{ isChangeTray ? "Change quantity" : "Choose your quantity" }}
        </Heading>
        <Text
          >Your branch will do their best to accomodate your selected lengths. If you don’t see the length you require
          <NuxtLink to="/help/contact-us" class="text-mkm-blue-light cursor-pointer">contact your branch</NuxtLink>.
        </Text>
      </Stack>
    </template>
    <aside class="pt-md flex flex-col justify-between relative min-h-full">
      <Stack direction="col" gap="none" class="h-full !p-[0px]">
        <Stack direction="col" gap="none" class="border-b-sm border-grey-light pb-sm mb-2xs w-full">
          <Text tag="h4" weight="semi" size="md" class="px-sm" data-test-id="tallyPanelProductTitle">
            {{ props.isChangeTray ? props.productInfo.name : props.productDefaultPricing.displayName }}
          </Text>
          <Text
            v-if="!props.price && !props.isChangeTray"
            tag="h4"
            weight="bold"
            size="md"
            class="mt-[10px] px-sm"
            data-test-id="tallyPanelSalePrice"
          >
            {{ toCurrency({ value: isVatIncluded ? (priceIncVatAttr as number) : (priceExcVatAttr as number) }) }}
            {{ productUnitTranslation(`${unit}` as ProductDetailsType) }}
          </Text>
          <Text v-else-if="!props.price && props.isChangeTray" tag="h4" weight="bold" size="md" class="mt-[10px] px-sm">
            {{
              toCurrency({
                value: isVatIncluded
                  ? (props.productInfo.price_inc_vat as number)
                  : (props.productInfo.price as number),
              })
            }}
            {{ productUnitTranslation(`${props.productInfo.unit}` as ProductDetailsType) }}
          </Text>
          <Text v-else tag="h4" weight="bold" size="md" class="mt-[10px] px-sm">
            {{
              toCurrency({
                value: isVatIncluded ? (props.price.price_inc_vat as number) : (props.price.price as number),
              })
            }}
            {{ productUnitTranslation(`${props.price.unit}` as ProductDetailsType) }}
          </Text>
        </Stack>
        <Stack direction="col" gap="2xs" class="w-full pb-xl">
          <div v-for="(item, index) in tallySizes" :key="index" class="w-full">
            <Stack justify="between" align="center" class="w-full px-sm pb-2xs border-b-sm border-grey-light">
              <Text weight="semi" data-test-id="tallyPanelItemTitle">{{ item.length }} m</Text>
              <!-- @ts-ignore -->
              <CardQuantityControl
                :key="props.productInfo.sku"
                :quantity="props.isChangeTray && item.quantity ? item.quantity : 0"
                :tally-selector="item.length"
                :tally-quantity="props.isChangeTray && item.quantity ? item.quantity : 0"
                :is-tally="true"
                data-test-id="quantity"
                @update-tally-total="calculateTallyTotal"
              />
            </Stack>
          </div>
        </Stack>
      </Stack>
    </aside>
    <template #footer>
      <Stack class="w-full pb-sm" gap="none" justify="between">
        <Text class="nowrap">Total length:&nbsp;</Text>
        <Text weight="bold" class="text-right" data-test-id="totalLength">{{ totalTallyLengthAsString }}</Text>
      </Stack>
      <Stack align="center" justify="between" class="w-full">
        <Button variant="neutral" size="md" class="!w-fit sm:!w-full" data-test-id="cancelButton" @click="closeTray"
          >CANCEL</Button
        >
        <Button
          :variant="isChangeTray ? 'secondary' : 'primary'"
          size="md"
          class="!w-full"
          :disabled="(!isChangeTray && totalTallyLength === 0) || isAdding"
          data-test-id="addToBasketButton"
          @click="onAddToBasket"
        >
          {{ isChangeTray ? "APPLY" : "ADD TO BASKET" }}
          <Spinner v-show="isAdding" />
        </Button>
      </Stack>
    </template>
  </LazyDrawer>
</template>

<script setup lang="ts">
import useTally from "~/composables/useTally";
import { toCurrency } from "~/../../packages/mkm-avengers/src/avengers";
import { productUnitTranslation, type ProductDetailsType } from "~/utils/productUnitTranslation";

const props = defineProps<{
  productInfo: any;
  price?: any;
  selectedBranchId: any;
  productDefaultPricing?: any;
  isChangeTray?: boolean;
}>();
const { setTallyTray, tallyTray, setBasketTray } = useUIState();
const { addCartItemQuantity, changeItemQuantity } = useCart();
const { isVatIncluded } = useUIState();
const quantity = ref<number>(0);
const isAdding = ref<boolean>(false);

const closeTray = () => {
  setTallyTray();
};
const { tallyOptions, fetchTallyOptions } = useTally();
const totalTallyLength = ref<number>(0);
type TallyAddedObjType = {
  [key: string]: number;
};
type TallyItemType = {
  length: string;
  quantity: number;
};
type TallyOptionType = {
  length: number;
  is_wish: boolean;
};
const tallyAddedObj = ref<TallyAddedObjType>({});
const tallySizes = ref<Array<{ length: string | number; quantity: number; is_wish?: boolean }> | null>(null);
const mergedSizes = ref<TallyItemType[] | null>(null);
const notAvailableTallyItems = ref<TallyItemType[] | null>(null);
const tallyOptionsWithoutQt = ref<TallyOptionType[] | null>(null);

const priceVatInc = ref<string>("");
const priceIncVatAttr = extractAttributeValue(props.productDefaultPricing?.customAttrs || [], "price_inc_vat");
priceVatInc.value = priceIncVatAttr;

const priceVatExc = ref<string>("");
const priceExcVatAttr = extractAttributeValue(props.productDefaultPricing?.customAttrs || [], "price");
priceVatExc.value = priceExcVatAttr;

const unit = ref<string>("");
const getUnit = extractAttributeValue(props.productDefaultPricing?.customAttrs || [], "unit");
unit.value = getUnit;

function extractAttributeValue(objects: any[], attributeName: string): any {
  const objectWithAttribute = objects.find((obj) => obj.name === attributeName);
  return objectWithAttribute ? objectWithAttribute.values[0] : null;
}

const totalTallyLengthAsString = computed(() => `${totalTallyLength.value.toFixed(1)} m`);

const checkTallyItemAvailability = (length: any) => {
  return notAvailableTallyItems.value && notAvailableTallyItems.value.some((item: any) => item["length"] === length);
};

//@ts-ignore
const calculateTallyTotal = (tallySelector: any, tallyValue: any) => {
  quantity.value = tallyValue;

  if (notAvailableTallyItems.value && checkTallyItemAvailability(tallySelector)) {
    return;
  } else {
    if (Number.isNaN(tallyValue)) {
      tallyValue = 0;
    }
    tallyAddedObj.value[tallySelector] = tallyValue;
    let totalLength = 0;
    Object.keys(tallyAddedObj.value).forEach((item) => {
      const itemValue = Number(tallyAddedObj.value[item]);
      const itemKey = Number(item);
      if (!Number.isNaN(itemValue) && !Number.isNaN(itemKey)) {
        totalLength += itemKey * itemValue;
      }
    });
    totalTallyLength.value = Number(totalLength.toFixed(2));
  }
};

const onAddToBasket = async () => {
  isAdding.value = true;
  const tallyParams = [];

  for (const [length, quantity] of Object.entries(tallyAddedObj.value)) {
    tallyParams.push(`${Number(quantity)}:${length}`);
  }
  const paramsString = tallyParams.join(",");
  if (props.isChangeTray) {
    await changeItemQuantity(props.productInfo.sku, 1, paramsString);
    isAdding.value = false;
    setTallyTray();
  } else {
    await addCartItemQuantity(props.productInfo.sku || props.productInfo.id, 1, paramsString).then(() => {
      isAdding.value = false;
      setTallyTray();
      setBasketTray(true);
    });
  }
};

const handleTallyTrayToggling = async () => {
  // Reset state before loading new tally options
  tallyAddedObj.value = {};
  totalTallyLength.value = 0;
  tallySizes.value = null;
  mergedSizes.value = null;
  notAvailableTallyItems.value = null;
  tallyOptionsWithoutQt.value = null;

  const productID = props.productInfo.sku || props.productInfo.id;
  if (!tallyTray || !productID) return;

  await fetchTallyOptions(productID, props.selectedBranchId);

  if (props.isChangeTray) {
    // eslint-disable-next-line no-unused-vars
    tallyOptionsWithoutQt.value = tallyOptions.value?.tally.map(({ quantity, ...rest }) => rest) ?? null;

    notAvailableTallyItems.value =
      props.productInfo.tally?.sizes.filter(
        (obj: any) => !tallyOptionsWithoutQt.value?.some((obj2) => obj.length === obj2.length),
      ) || null;

    mergedSizes.value =
      props.productInfo.tally?.sizes.concat(
        tallyOptionsWithoutQt.value?.filter(
          (item) => props.productInfo.tally?.sizes.every((itemCart: any) => itemCart.length !== item.length),
        ) || [],
      ) || null;
  }

  tallySizes.value = props.isChangeTray
    ? mergedSizes.value?.sort((a, b) => (a.length as any) - (b.length as any)) || null
    : tallyOptions.value?.tally || null;

  if (props.isChangeTray) {
    tallyAddedObj.value =
      tallySizes.value?.reduce((acc, item) => {
        acc[item.length] = item.quantity ?? 0;
        return acc;
      }, {} as TallyAddedObjType) || {};
  }
};

watch(tallyTray, handleTallyTrayToggling);

onMounted(handleTallyTrayToggling);
</script>
